import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import { ReactComponentElement, CSSProperties } from 'react';
import shortid from 'shortid';

export const getRandomId = shortid.generate;

/**
 * Sometimes children is rendered via craft, so it's an array,
 * other times is rendered directly so it's a react component...
 * @param children
 */
export const hasDescendants = (children?: Array<any> | ReactComponentElement<any>) => {
  if (!children) {
    return false;
  }
  if (isArray(children)) {
    return children.length > 0;
  }
  return children.props.children?.length > 0 ?? false;
};

const withUnit = (s?: string | number) => {
  if (!s) {
    return undefined;
  }
  if (isString(s) && s.indexOf('%') > -1) {
    return s;
  }
  if (isNumber(s) || parseFloat(s).toString() === s) {
    return `${s}px`;
  }
  return s;
};

const parseProperty = (value: string | number) => {
  const values = value.toString().split(' ');
  const singleValue = values.length === 1 ? values[0] : null;

  return {
    top: singleValue || values[0],
    right: singleValue || values[1],
    bottom: singleValue || values[2],
    left: singleValue || values[3],
  };
};

/**
 * When applying style object to a Node, it is possible for both
 * shorthand and specific version of same property to be defined
 * eg. margin + marginTop, if we try to apply them at the same time
 * browsers will complain about possible styling inconsistencies
 * @param style
 */
export const unpackShorthandStyles = ({
  padding,
  margin,
  ...other
}: CSSProperties): CSSProperties => {
  let output = {};
  if (padding) {
    const { top, right, bottom, left } = parseProperty(padding);
    output = {
      paddingTop: top,
      paddingRight: right,
      paddingBottom: bottom,
      paddingLeft: left,
    };
  }
  if (margin) {
    const { top, right, bottom, left } = parseProperty(margin);
    output = {
      ...output,
      marginTop: top,
      marginRight: right,
      marginBottom: bottom,
      marginLeft: left,
    };
  }
  const newCss = { ...output, ...other };
  Object.keys(newCss).forEach((key) => {
    if (key.indexOf('margin') === 0 || key.indexOf('padding') === 0) {
      // @ts-expect-error
      newCss[key] = withUnit(newCss[key] as string | number);
    }
  });
  return newCss;
};
